/**
 * What the Waves Tune plugin most likely has right now, for key AND scale,
 * from the two things that ever move it: a song change and our own tap.
 *
 * ## Why the scale is derived from the song's key
 *
 * On every song change that carries a readable `vocals_key`, OPBridge sends
 * BOTH CCs, key and scale (`handlers/setlist.rs::send_autotune` ->
 * `autotune::resolve`, read 2026-09-28). The scale it sends is the key's mode:
 * chromatic -> list entry 3, major -> 7, minor -> 10 - the same values as our
 * `SCALE_CC_VALUES` chrom / maj / min. So after a song change the plugin IS on
 * that scale, whatever was tapped before. This used to be a local-only latch on
 * the reasoning that a derived scale would misreport a tapped HARM or DOR as
 * plain minor; that only holds for a tap made AFTER the song, which the
 * newest-wins rule below handles.
 *
 * ## Newest wins, and a tap lights fully
 *
 * Whichever is newer - the song change or our tap - is what the plugin has,
 * and it is lit ACTIVE. A tap is not drawn as "pending": Waves reports nothing
 * back, so pending could never resolve and would read as a press that did not
 * work (operator, 2026-09-28: "if i press harm then i want the harm scale").
 * Ordering uses `appliedAt`, when this app first saw a song actually SENT,
 * never `at` (moves on every poll) and never the song's `generation` (moves on
 * every song the operator scrolls to with auto-sync off, which sends nothing:
 * until 2026-10-15 a scroll erased a tap the plugin still had).
 */

import { setlistState, songAutotune, type SetlistMirror, type VocalsKeyMode } from '../setlist/state.ts';
import { lastAutotuneKeySend, lastAutotuneScaleSend, type AutotuneScale } from './session.ts';

/** OPBridge's `Mode::scale_cc_value` -> our scale id, by equal CC value. */
const MODE_SCALE: Record<VocalsKeyMode, AutotuneScale> = {
  chromatic: 'chrom',
  major: 'maj',
  minor: 'min',
};

/** What the plugin was last told: the newer of the song and our tap, or null
 *  when neither has said anything. */
export function newestWins<T>(
  read: T | null,
  send: { value: T; at: number } | null,
  m: SetlistMirror,
): T | null {
  const songAt = m.status === 'ok' ? m.appliedAt : 0;
  return send && send.at > songAt ? send.value : read;
}

/** The song half comes from OPBridge's own outcome (`songAutotune`): lit only
 *  when the applied (last sent) song's key is the one its autotune leg parsed
 *  AND sent. */
export function autotuneKeyShown(m: SetlistMirror = setlistState()): number | null {
  const song = songAutotune(m);
  return newestWins(song?.pitchClass ?? null, lastAutotuneKeySend(), m);
}

export function autotuneScaleShown(m: SetlistMirror = setlistState()): AutotuneScale | null {
  const song = songAutotune(m);
  return newestWins(song ? MODE_SCALE[song.mode] : null, lastAutotuneScaleSend(), m);
}
